import React, { useMemo } from 'react';

import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { StarFilled, ClearOutlined } from '@ant-design/icons';

import { getExplicitFavourites } from 'selectors/search';
import { colours } from 'utils/theme';
import { clearCategoryFavouritesAction } from 'actions/search';
import { useDispatch } from 'react-redux';
import { Tooltip } from 'components/common';

/** Container for the entire starred section - sticky positioned for always-visible access */
const StarredContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 10; /* Ensure it stays above other dropdown content */
  padding-left: 12px;
  margin-bottom: 4px;
  border-bottom: 1px solid ${colours.borderGrey};
  background-color: ${colours.white};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06); /* Subtle shadow to separate from content below */
`;

/** Section title with star icon - styled to match filter section patterns */
const StarredTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  padding-top: 4px;
  color: ${colours.grey};
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

/** Container for the list of starred categories */
const StarredList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 160px; /* Limit height to ensure tree view remains accessible */
  overflow-y: auto; /* Enable vertical scrolling when content exceeds max-height */
`;

/** Individual starred category item with hover effects */
const StarredItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 14px;
  color: ${colours.black};
  transition: background-color 0.15s ease;
  &:hover {
    background-color: #f5f5f5;
  }
`;

const StarIcon = styled(StarFilled)`
  color: ${colours.gold};
  font-size: 14px;
  flex-shrink: 0; /* Prevent shrinking when text is long */
`;

/** Category name with ellipsis for long names */
const CategoryName = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
`;

const ClearIcon = styled(ClearOutlined)`
  color: ${colours.grey};
  font-size: 14px;
  flex-shrink: 0;
  margin-left: auto;
  margin-right: 20px;
  cursor: pointer;
  &:hover {
    color: ${colours.black};
  }
`;

/**
 * StarredSection Component
 *
 * A sticky section that displays user's favourite categories at the top of a dropdown.
 * Shows favourites in chronological order (oldest first) with tooltips showing full category paths.
 * Only renders when favourites exist to avoid empty sections.
 *
 * Features:
 * - Chronological ordering (oldest first, in order of selection)
 * - Full category path tooltips
 * - Visual distinction between explicit and implicit favourites
 * - Click handling for category selection
 * - Sticky positioning for easy access
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Function} props.onItemClick - Callback when a starred item is clicked
 * @param {Array<TreeItemModel>} props.allCategories - Complete category tree for building paths
 * @returns {JSX.Element|null} The starred section or null if no favourites
 */
const StarredSection = ({ onItemClick, allCategories = [] }) => {
  // Get favourites from Redux store - automatically sorted chronologically (oldest first)
  const rawFavourites = useSelector(getExplicitFavourites);
  const dispatch = useDispatch();

  // Filter out any favourites that aren't in the full category list that we're rendering.
  // Effectively stops showing favourites that aren't part of the current licence.
  const categoryKeysMap = useMemo(() => {
    const ckMap = {};
    for (let category of allCategories) {
      ckMap[category.value] = category;
    }
    return ckMap;
  }, [allCategories]);

  const favourites = useMemo(
    () =>
      rawFavourites.filter(
        favourite => !!categoryKeysMap[favourite.treeItemModelKey]
      ),
    [rawFavourites, categoryKeysMap]
  );

  const clearFavourites = () => {
    dispatch(clearCategoryFavouritesAction());
  };

  // Early return for empty favourites to avoid rendering empty section
  if (!favourites || favourites.length === 0) {
    return null;
  }

  return (
    <StarredContainer>
      <StarredTitle>
        <StarIcon />
        Favourite Categories
        <Tooltip title='Clear all favourites'>
          <ClearIcon onClick={clearFavourites} />
        </Tooltip>
      </StarredTitle>
      <StarredList>
        {favourites.map(({ treeItemModelKey }) => {
          return (
            <StarredItem
              key={treeItemModelKey}
              onClick={() => onItemClick && onItemClick(treeItemModelKey)}
            >
              <StarIcon />
              <CategoryName>
                {categoryKeysMap[treeItemModelKey].title}
              </CategoryName>
            </StarredItem>
          );
        })}
      </StarredList>
    </StarredContainer>
  );
};

export default StarredSection;
